import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { useApi } from 'services/api';
import type { paths } from 'types/schemas/api-schema';
import type { BookmarkType } from './types';
type BookmarkResponseMap = {
  id: paths['/bookmarks/id']['get']['responses']['200']['content']['application/json'];
  pql: paths['/bookmarks/pql']['get']['responses']['200']['content']['application/json'];
};
type BookmarkItem<T extends BookmarkType> = NonNullable<BookmarkResponseMap[T]['items']>[number];

const sortComparator = (a: BookmarkItem<'id'>, b: BookmarkItem<'id'>) =>
  new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();

export function useBookmarks<T extends BookmarkType>(type: T): UseQueryResult<BookmarkItem<T>[]>;

export function useBookmarks(type: BookmarkType = 'id') {
  const api = useApi();

  return useQuery({
    queryKey: ['bookmarks', type],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await api.get(`/bookmarks/${type}`);

      if (type === 'id') {
        const items = (response as BookmarkResponseMap['id']).items ?? [];
        return items.sort(sortComparator);
      }

      const items = (response as BookmarkResponseMap['pql']).items ?? [];
      return items.sort(sortComparator);
    },
    enabled: !!api,
  });
}
